<script setup>
import { useTheme } from '../composables/useTheme';
import { HrExpansionItem } from '../primitives';

/**
 * Navigation group — an expandable section header for grouping destinations in a side nav drawer.
 *
 * @status recommended
 * @group navigation
 * @useWhen Grouping related nav destinations under a collapsible header in a drawer
 * @avoidWhen Non-navigation collapsible content — use HrExpandableSection
 * @related HrNavItem
 * @within hr-list
 * @a11y Toggle button announces expanded/collapsed state; activates on Enter and Space.
 */
defineOptions({ name: 'HrNavGroup' });

defineProps({
  /**
   * Group header label text.
   * @example Settings
   */
  label: {
    type: String,
    default: null,
  },
  /**
   * Material icon name shown in the group header.
   * @example settings
   */
  icon: {
    type: String,
    default: null,
  },
  /** Optional secondary text shown below the label in the header. */
  caption: {
    type: String,
    default: null,
  },
  /** Expands the group on initial render without requiring user interaction. */
  defaultOpened: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the group. */
  disable: {
    type: Boolean,
    default: false,
  },
});

const { hasTheme } = useTheme();
</script>

<template>
  <hr-expansion-item
    :class="hasTheme ? 'hr-nav-group' : undefined"
    v-bind="{ ...$props, ...$attrs }"
  >
    <!-- @slot HrNavItem rows and nested HrNavGroup components. -->
    <slot />
  </hr-expansion-item>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/*
 * Quasar wraps both the header q-item and .q-expansion-item__content in a
 * .q-expansion-item__container div. Using two direct-child combinators pins
 * the selector to the immediate header row of THIS nav group only, so nested
 * nav groups are not accidentally styled by an ancestor's rule.
 */

/* ── Pill shape ──────────────────────────────────────────────────── */
.hr-nav-group :deep(> .q-expansion-item__container > .q-item) {
  margin: 0 12px;
  border-radius: var(--app-shape-corner-full, 9999px);
  overflow: hidden;
}

.hr-nav-group :deep(> .q-expansion-item__container > .q-item .q-item__section--avatar) {
  color:          var(--app-on-surface-variant);
}

/* ── Typography ──────────────────────────────────────────────────── */
.hr-nav-group :deep(> .q-expansion-item__container > .q-item .q-item__label) {
  font-size:      var(--app-type-label-large-size);
  line-height:    var(--app-type-label-large-line-height);
  font-weight:    var(--app-type-label-large-weight);
  letter-spacing: var(--app-type-label-large-tracking);
}

</style>
